import h from 'hyperscript';

import { FormElement } from 'ui/form-element/form-element';

import { assert } from 'cadenza/utils/custom-error';
import { ChangeEvent } from 'cadenza/utils/event-util';
import { uniqueId } from 'cadenza/utils/unique-id';

const COMPONENT_NAME = 'd-radio-group';

export interface LabelledValue<T extends string = string> {
  /** The content of the label element, which is inserted after the input element */
  label: string | Node;
  /** The value of the implicitly generated input element within the label element */
  value: T;
  /** If true, the disabled class will be added to the label element, the input element gets disabled */
  disabled?: boolean;
  /** Additional description that is displayed next to the label element and does not affect hovering */
  description?: Node;
}

export interface RadioGroupOptions<T extends string = string> {
  initialValue?: T;
  inline?: boolean;
  id?: string;
  name?: string;
  required?: boolean;
}

export class RadioGroup<T extends string = string> extends FormElement<T> {

  #values;
  #inline;

  /**
   * @param values - List of values with labels
   * @param [options] - Options
   * @param [options.initialValue] - The initial value, default is the first value
   * @param [options.inline] - Should radio buttons be displayed inline, default is false
   * @param [options.id] - The ID of this element
   * @param [options.name] - Radio group name, default is an auto-generated unique ID
   * @param [options.required] - Whether the radio group is initially required
   */
  constructor (values: LabelledValue<T>[], {
    initialValue = values[0].value,
    inline = false,
    id,
    name = uniqueId(),
    required
  }: RadioGroupOptions<T> = {}) {
    super({ defaultValue: initialValue, id, name, required });

    assert(values.map(value => value.value).includes(initialValue),
      'Values must include initialValue');

    this.#values = values;
    this.#inline = inline || false;
    this.addEventListener('change', this.#onChange);
  }

  override connectedCallback () {
    super.connectedCallback();
    this.classList.add(COMPONENT_NAME);
    if (!this.#initialized) {
      this.setAttribute('role', 'radiogroup');
      this.append(...this.#values.map(value => this.#createRadioOption(value)));
    }
  }

  get #initialized () {
    return (this.childNodes.length > 0);
  }

  #createRadioOption ({ value, label, disabled, description }: LabelledValue<T>) {
    const labelElement = h('label.d-form-check'
      + (this.#inline ? '.d-form-check-inline' : '')
      + ((this.disabled || disabled) ? '.is-disabled' : ''), [
      h('input', {
        type: 'radio',
        name: this.name,
        value,
        checked: value === this.defaultValue,
        disabled: this.disabled || disabled
      }),
      label
    ]);

    return description
      ? h('div.d-stack-h.space-1', {}, [ labelElement, description ])
      : labelElement;
  }

  override disconnectedCallback () {
    super.disconnectedCallback();
    this.defaultValue =
      this.value; // in case this component is (dis)connected multiple time, preserve the checked state
  }

  #onChange (event: Event) {
    if (event.target !== this) {
      event.stopImmediatePropagation();
      this.value = this.#checkedRadio.value as T;
      this.dispatchEvent(new ChangeEvent());
    }
  }

  override focus () {
    this.#checkedRadio?.focus();
  }

  override get value () {
    return super.value;
  }

  override set value (value) {
    const input = this.querySelector(`input[value="${value}"]`) as HTMLInputElement;
    if (input) {
      input.checked = true;
      super.value = value;
    } else if (!this.#initialized) {
      this.defaultValue = value;
    }
  }

  get #checkedRadio () {
    return this.querySelector('input:checked') as HTMLInputElement;
  }

  override applyDisabled (disabled: boolean) {
    this.querySelectorAll('input').forEach(input => {
      if (disabled) {
        input.dataset.previousDisabled = String(input.disabled);
        input.disabled = true;
      } else {
        input.disabled = input.dataset.previousDisabled === 'true';
      }
      const label = input.parentElement!;
      label.classList.toggle('is-disabled', input.disabled);
    });
  }

}

customElements.define(COMPONENT_NAME, RadioGroup);
